import { AppConstants } from './app-constants';
import { Configuration, VehicleList } from '../types/tagging';
import { Vehicle } from '../types/vehicle';
import { FeatureAppContent } from '../hooks/use-feature-app-content';
import { ModelConfig } from '../types/model';

/**
 * Recognizes if url has defined query params
 * Auxiliary Function
 * @param url
 * @returns boolean
 */
const hasQueryParams = (url: string) => {
  const queryParamsPattern = new RegExp(/\?.+=.*/g);
  return queryParamsPattern.test(url);
};

/**
 * Gets query params for E06 pov format
 * @param width
 * @param height
 * @param isNewFormat
 * @returns
 */
const getPovE06Params = (
  width: string,
  height: string,
  isNewFormat: boolean,
) => {
  return isNewFormat
    ? `&width=${Math.floor(+width * 1.2)}&height=${Math.floor(
      +height * 1.2,
    )}&cut=1&quality=90&crop=${Math.floor(+width * 0.06)},${Math.floor(
      +height * 0.47,
    )},${width},${height}`
    : `&h=4200&quality=90&w=7000&width=${width}&x=1050&y=3000`;
};

/**
 * Gets query params for E13 pov
 * @param width
 * @param height
 * @param isNewFormat
 * @returns string
 */
const getPovE13Params = (
  width: string,
  height: string,
  isNewFormat: boolean,
) => {
  return isNewFormat
    ? `&width=${Math.floor(+width * 1.2)}&height=${Math.floor(
      +height * 1.2,
    )}&cut=1&quality=90&crop=${Math.floor(+width * 0.07)},${Math.floor(
      +height * 0.47,
    )},${width},${height}`
    : `&h=4100&quality=90&w=7900&height=${height}&width=${width}&x=900&y=3000`; //accuracy
  // : `&h=4200&quality=90&w=7500&width=${width}&x=1250&y=3000`; // cropped
  // : `&h=4000&quality=90&w=8000&width=${width}&x=700&y=3000`; // No centered
  // : `&h=4200&quality=90&w=7500&width=300&x=1020&y=300`; // smaller;
};

/**
 * Give properties to the images from iris.
 * The width and height need to be proportional to 960*432,
 * for instance, 480*216 and so on
 * @param mediaImageUrl Image from iris
 * @param pov Image position
 * @param width Image width
 * @param height Image height
 * @param modelYear Vehicle's year
 * @param modelName Vehicle's model
 */
export const transformIrisUrl = (
  mediaImageUrl: string,
  pov: string,
  width = '960',
  height = '432',
  modelYear = '',
  modelName = '',
  isNewImage = false,
): string => {
  if (!mediaImageUrl) return '';

  // New Vicci images, encoded images...
  const urlQueryParams = new URLSearchParams(mediaImageUrl);
  const isIrisServer = mediaImageUrl.toLowerCase()?.includes('iris');

  if (modelName.trim().includes('ID. Buzz')) return mediaImageUrl;

  if (isNewImage || !urlQueryParams.has('pov')) {
    const newWidthParam =
      hasQueryParams(mediaImageUrl) || isIrisServer
        ? `&width=${width}`
        : `?width=${width}`;
    return mediaImageUrl + newWidthParam;
  }

  // No NGW6 Images
  const useNewFormat =
    modelYear &&
      modelName &&
      +modelYear >= 2020 &&
      modelName.toLocaleLowerCase() !== 'passat'
      ? true
      : false;

  const isPassat2021 =
    modelYear &&
      modelName &&
      +modelYear == 2021 &&
      modelName.toLocaleLowerCase() === 'passat'
      ? true
      : false;

  const regex = /pov=(.*?),/g;
  const baseUrl = mediaImageUrl.replace(regex, `pov=${pov},`);
  switch (pov) {
    case AppConstants.IrisImgPositionE13:
      return (
        baseUrl + getPovE13Params(width, height, useNewFormat || isPassat2021)
      );
    case AppConstants.IrisImgPositionE06:
      return (
        baseUrl + getPovE06Params(width, height, useNewFormat || isPassat2021)
      );
    default:
      return (
        baseUrl + getPovE13Params(width, height, useNewFormat || isPassat2021)
      );
  }
};


/**
 * Validate if is a valid hex color
 * @param hexColor Hex color format, you can use it without #
 */
export const isValidHexColor = (hexColor: string) => {
  const hexColorRegExp = /(^#?[a-fA-F0-9]{6}$|^#?[a-fA-F0-9]{3}$)/;

  return hexColorRegExp.test(hexColor);
};


/**
 * Convert the number to thousand format
 * @param value String with a number
 */
export const separateThousand = (value: string) => {
  const intValue = parseInt(value, 10);

  return intValue < 1000
    ? intValue
    : intValue.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Convert a word from plural to singular
 * @param text String with a plural word
 */
export const removePluralS = (text: string): string => {
  if(!text) return '';

  return text.replace(/s$/, '');
};

/**
 * Map vehicles to array for listing
 */
export const mapVehiclesToVehiclesList = (vehicles: Vehicle[]): VehicleList[] => {
  return vehicles.map((vehicle: Vehicle) => ({
    BodyType: vehicle.category,
    CarlineId: vehicle.carlineId,
    CarlineName: vehicle.name,
    Manufacturer: 'VW',
    ModelName: vehicle.name,
    ModelYear: Number(vehicle.modelYear),
    UsedVehicle: false,
  }));
};

/**
 * Map single vehicle to configuration object
 */
export const mapVehicleToConfiguration = (vehicle: Vehicle): Configuration => {
  const vehicleCategory: Record<string, string> = {
    "suvs" : "SUV",
    "sedans" : "Sedan",
    "compacts" : "Compact",
    "bus": "Bus"
  };

  return {
    BodyTypeName: vehicleCategory[vehicle.category] || vehicle.category,
    CarlineId: vehicle.carlineId,
    CarlineName: vehicle.name,
    Manufacturer: 'Volkswagen',
    ModelName: vehicle.name,
    ModelYear: vehicle.modelYear,
    UsedVehicle: false,
  };
};

/**
 * Map a vehicle content in faContent
 */
export const mapVehicleToFeatureAppContent = (featureAppContent : FeatureAppContent, vehicle: ModelConfig) => {
  switch(vehicle.category) {
    case "suvs":
      return featureAppContent.suvs?.find((contentVehicle) => contentVehicle.carlineId === vehicle.carlineId);
    case "sedans":
      return featureAppContent.sedans?.find((contentVehicle) => contentVehicle.carlineId === vehicle.carlineId);
    case "compacts":
      return featureAppContent.compacts?.find((contentVehicle) => contentVehicle.carlineId === vehicle.carlineId);
    case "bus":
      return featureAppContent.electric_vehicles?.find((contentVehicle) => contentVehicle.carlineId === vehicle.carlineId);
    default:
      return undefined;
  }
}
